import React, { PropsWithChildren, RefObject, useImperativeHandle, useRef } from 'react';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import { FileUploadParam } from '../../types/FileUploadParam';
import { InputProps } from '../../../../types/HtmlProps';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { Heading } from '../../../components/Heading/Heading';
import { KeyEvent } from '../../../../types/KeyEvent';

export type CombinedRefContent = {
  inputRef: RefObject<HTMLInputElement | undefined>;
  labelRef: RefObject<HTMLLabelElement | undefined>;
};

export type CombinedRef = RefObject<CombinedRefContent>;

type FileUpload = {
  headingLabel?: string;
  dropzoneText?: string;
  helpText?: string;
  selectButtonLabel?: string;
  param: FileUploadParam<string>;
  onSelectedFilesChanged?: React.ChangeEventHandler<HTMLInputElement> | undefined;
  ref: CombinedRef;
} & Omit<InputProps, 'id'>;

function normalizeParamsToHTMLAttributes(htmlFileUploadProps: InputProps, param: FileUploadParam<string>): InputProps {
  return {
    required: param.required,
    multiple: param.multiple,
    name: param.name,
    size: param.constraints?.maxFileSizeInBytes,
    accept: (param.constraints?.fileExtensions || [])
      .map(extension => `.${extension}`)
      .concat(param?.constraints?.mimeTypes || [])
      .join(','),
    ...htmlFileUploadProps,
  };
}

export function BaseFileUpload({
  dropzoneText,
  param,
  headingLabel,
  selectButtonLabel,
  onSelectedFilesChanged,
  children,
  ref,
  ...inputProps
}: PropsWithChildren<FileUpload>) {
  const { t } = useSonTranslation('FileUpload');
  const id = useHtmlValidId();

  const inputRef = useRef<HTMLInputElement>(null);
  const labelRef = useRef<HTMLLabelElement>(null);

  // combine ref and inputRef pointing to the same element, so that component also works without a ref prop but
  // if present caller of the component has access to inputRef
  useImperativeHandle(ref?.current.inputRef, () => inputRef.current as HTMLInputElement);
  useImperativeHandle(ref?.current.labelRef, () => labelRef.current as HTMLLabelElement);

  /**
   *  Accessibility: the upload function must be available with enter key on faked button
   */
  const handleOnKeyDown = (event: React.KeyboardEvent<any>) => {
    if ((event.code === KeyEvent.Enter || event.code === KeyEvent.Space) && inputRef.current) {
      event.preventDefault();
      inputRef.current.click();
    }
  };

  const htmlAttributes = normalizeParamsToHTMLAttributes(inputProps, param);

  return (
    <div className="file-upload">
      {headingLabel
        ? (
            <Heading hierarchy={3} className="attached-files-base__headline">
              {headingLabel}
            </Heading>
          )
        : null}
      <div className="file-upload__dropzone">
        <span className="file-upload__dropzone-text" aria-hidden>
          {dropzoneText || (param?.multiple ? t('dropzoneTextPlural') : t('dropzoneText'))}
        </span>
        <input
          className="file-upload__input"
          type="file"
          id={id}
          tabIndex={-1}
          aria-hidden="true"
          ref={inputRef}
          onChange={onSelectedFilesChanged}
          {...htmlAttributes}
        />
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className="file-upload__label" htmlFor={id}>
          <span className="file-upload__button" tabIndex={0} role="button" ref={labelRef} onKeyDown={handleOnKeyDown}>
            <span className="btn__text">
              {selectButtonLabel
                ? selectButtonLabel
                : htmlAttributes.multiple
                  ? t('fileSelectButtonLabel')
                  : t('fileSelectSingleButtonLabel')}
            </span>
          </span>
        </label>
      </div>
      {children}
    </div>
  );
}
